/**
 * Check now (lane feat-check-now): a per-company button that asks Moov and
 * Plaid for that company right now, then starts the booking step. It sits
 * beside the "Updated 2m ago · Refresh" caption, which is a different thing:
 * Refresh only re-reads our own database and asks no bank anything.
 *
 * Owner grant, verbatim (Mike, 21 Oct 2026): "me and account managers."
 *
 * Rules this component keeps:
 *  - One company at a time. With no company and no choices (the
 *    all-companies view) it renders nothing at all.
 *  - The server enforces who may press and the 10-minute wait; the UI hides
 *    nothing it is unsure of and never guesses the role.
 *  - Every click handler goes through runAnnouncedAction.
 *  - Anything the server says is shown as text (textContent), never HTML, with
 *    control and format characters replaced.
 *  - Polling is bounded: every 5 seconds, stopping at a terminal outcome, at 10
 *    minutes, when the page is no longer current, and not at all while the tab
 *    is hidden. There is no retry loop: a failed poll stops the polling.
 */
import { AdminApiError, type AdminClient } from "../api.js";
import { clear, h } from "../dom.js";
import { settleReads } from "../read-cache.js";
import type { CheckNowStatus, CompanySlug, StaffRole } from "../types.js";
import { advice } from "./capabilities.js";
import { failureCopy, isUnreachable, outcomeSentence } from "./failure.js";
import { readerRole } from "./facts.js";
import { disclosure, focusAfterWrite } from "./components.js";
import { timeAgoText } from "./display-words.js";
import { navLabel } from "./sentences.js";
import { runWrite } from "./write.js";
// B0-2: the tail of "N wallets could not be read." has one owner.
import { couldNotBeReadText } from "./measured-claim.js";
// rd R8-14: the one sentence for a failed booking run (Home and Connection
// status say the same words about the same run).
import { bookingFailedCopy } from "../pages/home.js";
import { navigateTo, notifyCheckSettled } from "./nav-bus.js";

export const CHECK_NOW_POLL_MS = 5_000;
export const CHECK_NOW_POLL_MAX_MS = 10 * 60 * 1000;
/** Round 2 (2): a read that failed is not a write that did not save. */
const READ_FAILED = "Could not read the last check. Try again.";
/** The shell's one sentence for a request that never reached GV Books. */
const READ_UNREACHABLE = "Couldn't reach GV Books. Check your connection and try again.";
/**
 * Round 5 (3): the read sentence ends in "Try again", and the server's ten
 * minute wait keeps the button disabled after a failed poll. Telling someone to
 * press a control they cannot press is the defect; while the wait holds, name
 * the one step that does work and does not depend on the button.
 */
const READ_FAILURE_COOLDOWN = "The check may still be running. Reload to see it.";

export interface CheckNowOptions {
  client: AdminClient;
  /** A single selected company (Accounts). Absent or null = no fixed company. */
  company?: string | null;
  /**
   * To review is fleet-wide and has no single company, so it offers a chooser
   * (slug to label). Nothing is preselected, so no press is one click away.
   * With neither a company nor choices the component renders nothing.
   */
  companyChoices?: ReadonlyMap<string, string>;
  root: HTMLElement;
  /** The role the page already knows (B2b-6); unknown reads as the AM. */
  role?: StaffRole;
  /** False once a newer navigation superseded this render; drop results. */
  stillCurrent: () => boolean;
  /** Where "see the alert" goes, when the host page can navigate. */
  openAlerts?: () => void;
  /**
   * rd R8-05: called once, when a press this component is watching reaches its
   * end (the run it started has finished or failed). The shell passes its
   * forced badge refresh here, so "N need a look in To review" is not left
   * beside a badge that still says something else. Optional and never called
   * for a press that was already finished when the page loaded.
   */
  onSettled?: () => void;
  /** Test seams. */
  now?: () => number;
  pollMs?: number;
  pollMaxMs?: number;
  isHidden?: () => boolean;
}

/**
 * The write fingerprint for one company's press (B2b: a slug inside a template
 * is what the brand guard cannot tell from a leak). It is an internal key, never
 * words: `runWrite` uses it to hold one flight per company, and nothing renders
 * it. One place turns the slug into a key, so there is one recorded use.
 */
function pressKey(company: CompanySlug): string {
  return `check-now:${company}`;
}

/**
 * A time with its day when it is not recent. `clock` alone printed "11:01 AM"
 * for a press that was three days old, which reads as today (integration review
 * N7). Only a time still to come uses it: the wait for the next press is always
 * minutes away, so no date is wanted there.
 */
function clock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "a moment ago";
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

function nextAllowedFrom(error: AdminApiError): string | null {
  const body = error.body;
  if (body === null || typeof body !== "object") return null;
  const err = (body as { error?: unknown }).error;
  if (err === null || typeof err !== "object") return null;
  const details = (err as { details?: unknown }).details;
  if (details === null || typeof details !== "object") return null;
  const value = (details as { nextAllowedAt?: unknown }).nextAllowedAt;
  return typeof value === "string" && !Number.isNaN(Date.parse(value)) ? value : null;
}

/** True when nothing more will change about this press without a new press. */
export function checkNowIsTerminal(status: CheckNowStatus): boolean {
  const press = status.press;
  if (press === null) return true;
  if (press.status === "running") return false;
  if (press.sync?.state === "skipped_run_in_progress") return true;
  return press.outcome.state !== "booking" && press.outcome.state !== "reading";
}

/**
 * The sentence for a finished press, and whether it already names the cooldown
 * time (rd Z4 round-2 MUST-FIX 5c: "Try again in a few minutes." beside "Can
 * check again at 3:05 PM" contradicted itself).
 */
interface OutcomePhrase {
  text: string;
  namesRetryAt: boolean;
}

function outcomePhrase(
  status: CheckNowStatus,
  options: {
    alertAvailable: boolean;
    role: StaffRole;
    retryAt: string | null;
    /** rd R8-14: the BOOKING run is the run that failed, not another step. */
    bookingRunFailed: boolean;
  },
): OutcomePhrase | null {
  const press = status.press;
  if (press === null) return null;
  const outcome = press.outcome;
  const plain = (text: string): OutcomePhrase => ({ text, namesRetryAt: false });
  switch (outcome.state) {
    case "reading":
      return plain("Checking…");
    case "booking":
      if (press.sync?.state === "skipped_run_in_progress") {
        return plain(
          "The books are being updated right now. What was just read will be included in the next update.",
        );
      }
      return plain("Booking started");
    case "booked":
      // rd R8-04: the number counts events the run processed, not bookings
      // (removed-never-booked drains and duplicates are in it), so it must not
      // say "booked" — "Booked only when there is a booking".
      return plain(
        `${plural(outcome.newlyBooked ?? 0, "new transaction", "new transactions")} processed`,
      );
    case "nothing_new":
      // "Nothing new" alone was read beside lines the run had just put in To
      // review (rd Z4 B9-H2): say what was NOT new.
      return plain("Nothing new processed");
    case "could_not_finish":
      // rd R8-14: a failed BOOKING run is the state the shared sentences
      // decide (rd-z-fixes-background §3); Home and Connection status say the
      // same words about the same run. Any other failure keeps its own next
      // step and never calls itself a booking run.
      if (options.bookingRunFailed) return plain(bookingFailedCopy(options.role));
      // "see the alert" only where the host page has an alert to open (B9-M2):
      // Home, Bank accounts and Books have none, and a pointer to nothing is a
      // dead end. Without one, the sentence ends at the fact plus the next step
      // for the role reading it — and that step uses the wait the page already
      // knows, so it cannot disagree with the line beside it (5c).
      if (options.alertAvailable) return plain("Could not finish — see the alert");
      // The wait is the same for both roles, and it wins: telling a person to
      // press a control the server's ten-minute wait still holds is the round-5
      // (3) defect. Without a wait, the next step is the control that works —
      // Check now is the account manager's too, so neither role is told to ask.
      if (options.retryAt !== null) {
        return {
          text: `Could not finish. Try again at ${options.retryAt}.`,
          namesRetryAt: true,
        };
      }
      return plain(`Could not finish. ${advice("check_now", options.role)}`);
    case "not_seen":
      return plain("The booking run has not started. Open the run to see why.");
  }
}

function newLines(status: CheckNowStatus): number | null {
  const reads = status.press?.reads ?? null;
  if (reads === null) return null;
  return reads.moov.newMovements + reads.plaid.newOrChangedLines;
}

/**
 * rd R8-14: the booking run failed when the press itself ended cleanly, the
 * booking step it started ran, and that run is the one that failed. The Worker
 * writes every other failure with `sync: not_started` (a read, a dispatch, a
 * budget), so "the booking run failed" is never said about those.
 */
function bookingRunFailed(status: CheckNowStatus): boolean {
  const press = status.press;
  if (press === null) return false;
  return press.outcome.state === "could_not_finish" && press.sync?.state === "started";
}

/** What was read, in plain words. Counts only. */
function readLines(status: CheckNowStatus): string[] {
  const reads = status.press?.reads ?? null;
  if (reads === null) return [];
  const lines: string[] = [];
  lines.push(
    `Read ${plural(reads.moov.walletsRead, "wallet", "wallets")} and ${plural(
      reads.plaid.itemsRead,
      "bank connection",
      "bank connections",
    )}. ${plural(newLines(status) ?? 0, "new or changed line", "new or changed lines")}.`,
  );
  if (reads.moov.walletsSkipped > 0) {
    lines.push(couldNotBeReadText(plural(reads.moov.walletsSkipped, "wallet", "wallets")));
  }
  if (reads.moov.state === "failed" && reads.moov.walletsSkipped === 0) {
    lines.push("The wallets could not be read.");
  }
  if (reads.plaid.itemsFailed > 0) {
    lines.push(
      couldNotBeReadText(plural(reads.plaid.itemsFailed, "bank connection", "bank connections")),
    );
  }
  if (reads.plaid.itemsNeedReconnect > 0) {
    lines.push(
      `${plural(reads.plaid.itemsNeedReconnect, "bank connection needs", "bank connections need")} to be reconnected.`,
    );
  }
  if (reads.moov.mayHaveMore) {
    // rd R8-20b: "Moov returned a full page" was the wire's own words.
    lines.push("There may be more wallet activity than was read.");
  }
  return lines;
}

function safeRunHref(url: string | null): string | null {
  return url !== null && url.startsWith("https://github.com/") ? url : null;
}

export function renderCheckNow(opts: CheckNowOptions): void {
  const { client, root, stillCurrent } = opts;
  clear(root);
  const choices = opts.companyChoices;
  const fixed = opts.company ?? null;
  // The all-companies view: nothing to check, so nothing to show.
  if (fixed === null && (choices === undefined || choices.size === 0)) return;

  const role: StaffRole = readerRole(opts.role);
  const now = opts.now ?? Date.now;
  const pollMs = opts.pollMs ?? CHECK_NOW_POLL_MS;
  const pollMaxMs = opts.pollMaxMs ?? CHECK_NOW_POLL_MAX_MS;
  const isHidden = opts.isHidden ?? ((): boolean => document.hidden);
  /** The console's one time phrase: "5 minutes ago", else "Oct 25, 11:01 AM". */
  const when = (iso: string): string => timeAgoText(iso, new Date(now())) || "some time ago";

  let selected: string | null = fixed;
  let busy = false;
  let blockedUntil: number | null = null;
  let loadToken = 0;
  let pollGeneration = 0;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;
  let windowTimer: ReturnType<typeof setTimeout> | undefined;
  let visibilityHandler: (() => void) | undefined;
  /** rd R8-05: one announcement per component, not one per poll. */
  let settled = false;
  /**
   * Round 5 (3): the sentence a failed read wants on screen once the button can
   * be pressed. `paint()` and `load()` clear it, because only a failure still
   * describes the current state. Null means "no read failure is being shown".
   */
  let readFailure: string | null = null;
  // A refused read (403) is about who may read, not about a run: reloading
  // cannot help, so its sentence wins over the cooldown wording.
  let readFailureIsAccess = false;
  // The company the in-flight press was made for. A press owns the status only
  // while that company is still the one on screen.
  let pressCompany: string | null = null;

  /**
   * Round 3 (1): a failed READ is not a write that did not save, on the first
   * load and on every poll alike. One helper, so the two paths cannot drift
   * into "That did not save." A server that answered earns the read sentence;
   * a request that never reached GV Books names the connection.
   */
  function showReadFailure(error: unknown): void {
    // A press in flight owns the status: its own answer (saved, refused, or
    // the wait) is what the person asked for, so a read that fails underneath
    // it says nothing (round 5 review, MUST-FIX 1).
    if (busy && pressCompany === selected) return;
    // Round 4 (4): the detail card describes the run that was last painted. A
    // read that failed cannot say that run is still going, so nothing of it is
    // left on screen to be read as current.
    clear(detail);
    // Round 5 (2): the row below the card carried the run's "Open the booking
    // run" link. That link says a run is there to open now, which is the same
    // claim the card made — with the card gone it is a dangling invitation, so
    // it goes too.
    clear(links);
    // Round 4 (4): a 403 is about who may read this company, not about the
    // network. decision 1: the owner reads "You do not have access to do that.";
    // the account manager is told the one thing they can do.
    if (error instanceof AdminApiError && error.status === 403) {
      readFailure = failureCopy(error, { role, context: "read" }).sentence;
      readFailureIsAccess = true;
    } else {
      readFailure = isUnreachable(error) ? READ_UNREACHABLE : READ_FAILED;
      readFailureIsAccess = false;
    }
    // Round 5 (3): the button may still be in the server's wait, so the status
    // is written where the wait is known — `refreshButton` owns both.
    refreshButton();
  }

  const button = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "check-now",
      "aria-label": "Check now: ask the bank and wallet for this company right now",
    },
    ["Check now"],
  );
  const status = h("p", {
    class: "muted caption",
    role: "status",
    "aria-live": "polite",
    "data-role": "check-now-status",
  });
  const detail = h("ul", { class: "muted caption", "data-role": "check-now-detail" });
  /**
   * round-11 item 24: a 409's own words are detail, never a face. The decided
   * sentence says what happened; the server's scrubbed message lives here.
   */
  const conflictDetail = h("div", { "data-role": "check-now-conflict-detail" });
  const links = h("p", { class: "caption", "data-role": "check-now-links" });

  const rowChildren: Array<HTMLElement> = [];
  let chooser: HTMLSelectElement | null = null;
  if (fixed === null && choices !== undefined) {
    chooser = h("select", { "data-role": "check-now-company", "aria-label": "Company to check" }, [
      h("option", { value: "" }, ["Choose a company"]),
      ...[...choices.entries()].map(([slug, label]) => h("option", { value: slug }, [label])),
    ]);
    rowChildren.push(h("label", { class: "caption" }, ["Check now for ", chooser]));
  }
  rowChildren.push(button);
  const section = h("section", { class: "check-now", "data-role": "check-now" }, [
    h("div", { class: "form-row" }, rowChildren),
    status,
    conflictDetail,
    detail,
    links,
  ]);
  root.append(section);

  function refreshButton(): void {
    const waiting = blockedUntil !== null && now() < blockedUntil;
    const off = busy || selected === null || waiting;
    button.disabled = off;
    if (off) button.setAttribute("disabled", "true");
    else button.removeAttribute("disabled");
    // Round 5 (3): a read failure is said once, here, because this is the only
    // place that knows whether the button can be pressed. The read sentence
    // invites a press; while the wait holds there is nothing to press, so the
    // status names the step that works instead. When the wait ends this runs
    // again and the read sentence comes back on its own.
    if (readFailure !== null) {
      status.textContent = waiting && !readFailureIsAccess ? READ_FAILURE_COOLDOWN : readFailure;
    }
    if (windowTimer !== undefined) {
      clearTimeout(windowTimer);
      windowTimer = undefined;
    }
    if (waiting && blockedUntil !== null) {
      // One timer, no loop: when the wait ends the button comes back.
      // The delay is clamped: setTimeout overflows (and fires at once) past
      // about 24 days, and a wait is never longer than ten minutes anyway.
      windowTimer = setTimeout(() => {
        windowTimer = undefined;
        if (!stillCurrent()) return;
        refreshButton();
      }, Math.min(Math.max(0, blockedUntil - now()), pollMaxMs));
    }
  }

  function stopPolling(): void {
    pollGeneration += 1;
    if (pollTimer !== undefined) {
      clearTimeout(pollTimer);
      pollTimer = undefined;
    }
    if (visibilityHandler !== undefined) {
      document.removeEventListener("visibilitychange", visibilityHandler);
      visibilityHandler = undefined;
    }
  }

  /**
   * rd R8-05: tell the host that the run this component watched is over, once.
   * A press already finished when the page loaded is not "settled" here: no
   * run finished while the person was looking, so nothing needs a fresh badge.
   *
   * Round 2: the shell is reached through the nav bus, because five pages mount
   * this component and none of them owns the badge. `onSettled` stays for a
   * host that wants its own callback; the bus call is the shell's.
   */
  function announceSettled(): void {
    if (settled) return;
    settled = true;
    opts.onSettled?.();
    notifyCheckSettled();
  }

  function paint(next: CheckNowStatus): void {
    const press = next.press;
    // Round 5 (3): a read that answered is the current state, so any sentence
    // about a read that failed is no longer true.
    readFailure = null;
    blockedUntil = next.nextAllowedAt === null ? null : Date.parse(next.nextAllowedAt);
    clear(detail);
    clear(links);
    const segments: string[] = [];
    // The wait the page can read, in the words the line beside it uses, so the
    // next step and the cooldown cannot disagree (5c).
    const retryAt =
      next.nextAllowedAt !== null && blockedUntil !== null && now() < blockedUntil
        ? clock(next.nextAllowedAt)
        : null;
    let phraseNamesRetryAt = false;
    if (press === null) {
      segments.push("Not checked yet");
    } else if (press.outcome.state === "reading") {
      segments.push("Checking…");
    } else {
      segments.push(`Last checked ${when(press.completedAt ?? press.requestedAt)}`);
      const lines = newLines(next);
      if (lines !== null) {
        segments.push(plural(lines, "new or changed line", "new or changed lines"));
      }
      const phrase = outcomePhrase(next, {
        alertAvailable: opts.openAlerts !== undefined,
        role,
        retryAt,
        bookingRunFailed: bookingRunFailed(next),
      });
      if (phrase !== null) {
        segments.push(phrase.text);
        phraseNamesRetryAt = phrase.namesRetryAt;
      }
    }
    // A run can put lines in To review without booking anything (the Worker's
    // `nothing_new` state carries `newForReview` too), so this is said in every
    // state, not only `booked` (rd Z4 B9-H2).
    const forReview = press?.outcome.newForReview ?? 0;
    if (press !== null && forReview > 0) {
      segments.push(`${String(forReview)} need a look in ${navLabel("queue")}`);
    }
    // Said once: when the next step above already named the time, repeating it
    // here is the same fact twice in two wordings (5c).
    if (retryAt !== null && !phraseNamesRetryAt) {
      segments.push(`Can check again at ${retryAt}`);
    }
    status.textContent = segments.join(" · ");
    for (const line of readLines(next)) detail.append(h("li", {}, [line]));
    const href = safeRunHref(press?.sync?.runUrl ?? null);
    if (href !== null && press?.sync?.state === "started") {
      const startedAt = press.sync.startedAt;
      const label =
        startedAt === null
          ? "Open the booking run"
          : `Open the booking run (a booking run started ${when(startedAt)})`;
      const anchor = h("a", { href, class: "link", "data-role": "check-now-run" }, [label]);
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
      links.append(anchor);
    }
    if (press !== null && forReview > 0) {
      // The count above names where the lines are; this opens it.
      const review = h(
        "button",
        { type: "button", class: "btn btn-ghost", "data-action": "check-now-review" },
        [`Open ${navLabel("queue")}`],
      );
      review.addEventListener("click", () => {
        navigateTo("queue");
      });
      if (links.childNodes.length > 0) links.append(document.createTextNode(" · "));
      links.append(review);
    }
    if (press !== null && press.outcome.state === "could_not_finish" && opts.openAlerts !== undefined) {
      const open = opts.openAlerts;
      const see = h("button", { type: "button", class: "btn btn-ghost", "data-action": "check-now-alert" }, [
        "See the alert",
      ]);
      see.addEventListener("click", () => {
        open();
      });
      if (links.childNodes.length > 0) links.append(document.createTextNode(" · "));
      links.append(see);
    }
    refreshButton();
  }

  function startPolling(company: CompanySlug): void {
    stopPolling();
    const generation = pollGeneration;
    const startedAt = now();
    const schedule = (): void => {
      pollTimer = setTimeout(tick, pollMs);
    };
    const tick = (): void => {
      pollTimer = undefined;
      if (generation !== pollGeneration || !stillCurrent() || selected !== company) {
        stopPolling();
        return;
      }
      if (now() - startedAt >= pollMaxMs) {
        stopPolling();
        // Gave up watching: the run may have finished or may still finish.
        settleReads(client);
        return;
      }
      if (isHidden()) {
        // No polling while the tab is hidden; resume once, when it is shown.
        const handler = (): void => {
          if (isHidden()) return;
          document.removeEventListener("visibilitychange", handler);
          visibilityHandler = undefined;
          if (generation === pollGeneration) tick();
        };
        visibilityHandler = handler;
        document.addEventListener("visibilitychange", handler);
        return;
      }
      void pollStatus();
    };
    /**
     * One poll. A failed read says so through `showReadFailure` (itself worded
     * by `failureCopy`), never by the shared write announcer: this page's words
     * now come from the one tables, on both the poll and the load.
     */
    const pollStatus = async (): Promise<void> => {
      let next: CheckNowStatus;
      try {
        next = await client.getCheckNow(company);
      } catch (error) {
        // The poll ends here (nothing reschedules it). Round 3 (1): the failure
        // is a read, so it says so — never "That did not save."
        settleReads(client);
        if (generation !== pollGeneration || !stillCurrent() || selected !== company) return;
        showReadFailure(error);
        return;
      }
      if (generation !== pollGeneration || !stillCurrent() || selected !== company) return;
      paint(next);
      if (checkNowIsTerminal(next)) {
        stopPolling();
        // The run this poll watched has finished: what it booked must show.
        settleReads(client);
        announceSettled();
        return;
      }
      schedule();
    };
    schedule();
  }

  function load(): void {
    const company = selected;
    // Round 2 (3): every load is a new watch, so a run that ends after it may
    // announce again.
    settled = false;
    stopPolling();
    clear(detail);
    clear(links);
    // Round 5 (3): a new load re-reads the truth, so the sentence about the
    // read that failed is retired before anything is painted.
    readFailure = null;
    // Coming back to a company whose press is still in flight: that press is
    // what is happening, so say so rather than leave the line blank.
    status.textContent = busy && pressCompany === company ? "Checking…" : "";
    blockedUntil = null;
    refreshButton();
    if (company === null) return;
    loadToken += 1;
    const token = loadToken;
    void loadStatus(company, token);
  }

  /** One load. A failed read says so through `showReadFailure`, never a save. */
  async function loadStatus(company: CompanySlug, token: number): Promise<void> {
    let current: CheckNowStatus;
    try {
      current = await client.getCheckNow(company);
    } catch (error) {
      // Round 2 (2): a failed read never says a save failed. A request that
      // never reached GV Books gets the shell's connection sentence.
      if (token !== loadToken || !stillCurrent() || selected !== company) return;
      showReadFailure(error);
      return;
    }
    if (token !== loadToken || !stillCurrent() || selected !== company) return;
    // A press for this company is the newer truth; an older read must not paint
    // over it or over its "Checking…".
    if (busy && pressCompany === company) return;
    paint(current);
    if (!checkNowIsTerminal(current)) startPolling(company);
  }

  button.addEventListener("click", () => {
    void press();
  });

  /**
   * One press, one request. `runWrite` holds one flight per company and disables
   * the whole row while it runs, so a double click, a held key or a shortcut
   * sends once. The words for every outcome come from the one tables
   * (`failureCopy` / `outcomeSentence`), never from the server's message.
   */
  async function press(): Promise<void> {
    const company: CompanySlug | null = selected;
    if (company === null) return;
    pressCompany = company;
    // Any read still in flight was asked before this press: retire it.
    loadToken += 1;
    // Round 2 (3): each press is its own run; the next terminal result it
    // reaches is announced, so two presses settle twice.
    settled = false;
    // Round 5 (3): the press replaces whatever a failed read said, so the
    // pending read sentence is retired before "Checking…" is written.
    readFailure = null;
    busy = true;
    stopPolling();
    clear(detail);
    clear(links);
    clear(conflictDetail);
    status.textContent = "Checking…";
    refreshButton();
    try {
      const outcome = await runWrite({
        key: pressKey(company),
        scope: section,
        gate: () => (selected === null ? "Choose a company first." : null),
        send: () => client.requestCheckNow(company),
        afterLanded: (result) => {
          if (selected !== company || !stillCurrent()) return;
          loadToken += 1;
          paint(result);
          // The press disables this button, so a keyboard user's focus fell to
          // the page body (rd Z4 9(d)). The result line is where the answer is.
          focusAfterWrite(status);
          if (!checkNowIsTerminal(result)) startPolling(company);
          else announceSettled();
        },
        stillCurrent,
        live: status,
      });
      // A press made for another company says nothing here: the company on
      // screen owns the status, and coming back re-reads the truth.
      if (selected !== company || !stillCurrent()) return;
      if (outcome.kind === "landed") return;
      if (outcome.kind === "refused" && outcome.status === 0) return;
      const error = outcome.error;
      if (outcome.kind === "refused" && outcome.status === 429) {
        const when = error instanceof AdminApiError ? nextAllowedFrom(error) : null;
        blockedUntil = when === null ? now() + 60_000 : Date.parse(when);
        status.textContent =
          when === null
            ? "A check is already running or was just done."
            : `A check is already running or was just done. It can run again at ${clock(when)}.`;
        return;
      }
      if (error !== undefined) {
        // 409 included: the decided sentence, and the server's own words only
        // behind "Show details".
        const copy = failureCopy(error, { role, context: "write" });
        status.textContent = copy.sentence;
        clear(conflictDetail);
        if (copy.detail !== null) {
          conflictDetail.append(disclosure("Show details", h("p", {}, [copy.detail])));
        }
        return;
      }
      status.textContent = outcomeSentence(outcome, role);
    } finally {
      busy = false;
      if (stillCurrent()) refreshButton();
    }
  }

  const chooserEl = chooser;
  chooserEl?.addEventListener("change", () => {
    selected = chooserEl.value === "" ? null : chooserEl.value;
    load();
  });

  refreshButton();
  if (selected !== null) load();
}
